// @flow

import type { Dispatch } from 'redux';
import {
  SET_PDB_ID, UNSET_PDB_ID, UNSET_ALL_PDB_IDS, RECEIVE_PDB_IDS,
} from './constants';
import { getActivePdbId, getPrice } from '../pdb/selectors';
import { getPdbIds, isPdbIdInComparison } from './selectors';
import {
  storePdbIdInStorage,
  getPdbIdsFromStorage,
} from './helpers';
import { getUiSetting, setUiSetting, toggleUiBooleanSetting } from '../uiStore';
import { pdbItemExists, fetchPdbData } from '../pdb';
import { compare } from '../../helpers';
import { selectDefaultConfiguration } from '../configuration';

const PDB_COMPARE_UI_KEY = 'pdb-compare-open';
export const getIsCompareOpen = (state: any) => getUiSetting(state, PDB_COMPARE_UI_KEY);
export const setCompareOpen = () => setUiSetting(PDB_COMPARE_UI_KEY, true);
export const setCompareClose = () => setUiSetting(PDB_COMPARE_UI_KEY, false);
export const toggleCompareOpen = () => toggleUiBooleanSetting(PDB_COMPARE_UI_KEY);

export type SetPdbId = {
  type: typeof SET_PDB_ID,
  pdbId: string,
};

export const setPdbId = (id: string): SetPdbId => ({
  type: SET_PDB_ID,
  pdbId: id,
});

export type UnsetPdbId = {
  type: typeof UNSET_PDB_ID,
  pdbId: string,
};

export const unsetPdbId = (id: string): UnsetPdbId => ({
  type: UNSET_PDB_ID,
  pdbId: id,
});

export type UnsetAllPdbIds = {
  type: typeof UNSET_ALL_PDB_IDS,
};

export const unsetAllPdbIds = (): UnsetAllPdbIds => ({
  type: UNSET_ALL_PDB_IDS,
});

export const unsetAllPdbIdsSetCompareClose = () => (dispatch: Dispatch<*>) => {
  dispatch(unsetAllPdbIds());
  dispatch(setCompareClose());
  storePdbIdInStorage([]);
};

export type ReceivePdbIds = {
  type: typeof RECEIVE_PDB_IDS,
  payload: Array<string>,
};

export const receivePdbIds = (pdbIds: Array<string>) => ({
  type: RECEIVE_PDB_IDS,
  payload: pdbIds,
});

/**
 * Note this method can only be called on the client side!
 */
export const hydrateComparison = () => (dispatch: Dispatch<*>) => (
  dispatch(receivePdbIds(getPdbIdsFromStorage()))
);


export const fetchPdbItems = (pdbIds: ?Array<string>) => (
  async (dispatch: Dispatch<any>, getState: Function) => {
    const state = getState();
    const ids = pdbIds || getPdbIds(state);

    await Promise.all(
      ids.map(pdbId => dispatch(fetchPdbData()(parseInt(pdbId, 10))).catch(() => {
        dispatch(unsetPdbId(pdbId));
        storePdbIdInStorage(getPdbIds(getState()));
      })).filter(pdbId => !pdbItemExists(state, pdbId)),
    );
  }
);


export const sortCompareItems = () => (
  async (dispatch: Dispatch<*>, getState: Function) => {
    const state = getState();
    const pdbIds = getPdbIds(state);

    // apply sorting async
    await fetchPdbItems(pdbIds);

    // now apply sorting
    pdbIds.sort((pdbA, pdbB) => compare(getPrice(state, pdbA), getPrice(state, pdbB), 'asc'));

    dispatch(receivePdbIds([...pdbIds]));
    storePdbIdInStorage(pdbIds);
  }
);

export const toggleActivePdbId = (pdbId: ?string) => (
  async (dispatch: Function, getState: Function): Promise<void> => {
    const state = getState();

    let id = pdbId;
    if (!pdbId) {
      id = getActivePdbId(state);
    }

    if (!id) {
      return;
    }

    if (isPdbIdInComparison(state, id)) {
      dispatch(unsetPdbId(id));
      dispatch(sortCompareItems());
      const activePdbIds = getPdbIds(getState());
      storePdbIdInStorage(activePdbIds);
      if (activePdbIds.length === 0) {
        dispatch(setCompareClose());
      }
      return;
    }

    dispatch(setPdbId(id));
    dispatch(sortCompareItems());
    const activePdbIds = getPdbIds(getState());
    storePdbIdInStorage(activePdbIds);
    const amount = activePdbIds.length;
    if (amount >= 2) {
      dispatch(setCompareOpen());
    }
  }
);

export const selectDefaultConfigForPdbIds = () => (
  async (dispatch: Function, getState: Function) => {
    const state = getState();
    const pdbIds = getPdbIds(state);

    await fetchPdbItems(pdbIds);

    await pdbIds.forEach(pdbId => dispatch(selectDefaultConfiguration(false, pdbId)));
  }
);

export type Actions = SetPdbId | UnsetPdbId | ReceivePdbIds | UnsetAllPdbIds;
